import { Fragment, useMemo, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRestApi } from 'context/RestApiContext';
import StatusLableComponent from 'components/Input/StatusLableComponent';

function PostContractQuoteInfo(props) {
  const restApi = useRestApi();
  const location = useLocation();
  const navigate = useNavigate();

  const postContractList = props.postContractList ?? []; // 契約後見積一覧
  const selectedSeikyuNo = props.selectedSeikyuNo; // 請求NO
  const contractList = props.contractList ?? []; // 契約一覧
  const callback = props.callback; // 再読み込みコールバック

  // 系列ごとの開閉状態
  const [expanded, setExpanded] = useState({});

  // ---- wijmoグリッドに合わせた共通スタイル ----
  const BORDER = '1px solid rgba(0,0,0,.2)';
  const FONT = 'ヒラギノ角ゴシック, メイリオ';
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    borderTop: BORDER,
    borderLeft: BORDER,
    fontSize: 12,
    fontFamily: FONT,
    tableLayout: 'fixed',
  };
  const thStyle = {
    background: '#eee',
    color: '#444',
    fontWeight: 700,
    borderRight: BORDER,
    borderBottom: BORDER,
    padding: '3px 6px',
    height: 25,
    whiteSpace: 'nowrap',
    fontFamily: FONT,
  };
  const tdBase = {
    borderRight: BORDER,
    borderBottom: BORDER,
    padding: '3px 6px',
    height: 38,
    verticalAlign: 'middle',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontFamily: FONT,
  };

  // パターンごとにまとめる
  const patternList = useMemo(() => {
    const map = new Map();
    postContractList.forEach((row) => {
      const key = row.PATTERN_NO;
      if (!map.has(key)) {
        map.set(key, { patternNo: key, rows: [] });
      }
      map.get(key).rows.push(row);
    });
    return [...map.values()]
      .sort((a, b) => a.patternNo - b.patternNo)
      .map((p) => {
        const sorted = [...p.rows].sort((a, b) => b.REV_NO - a.REV_NO); // 版の降順
        return {
          ...p,
          latest: sorted[0], // 最新版
          history: sorted.slice(1), // 旧版
          adoptedRow: p.rows.find((r) => r.ADOPTED_FLG == 1),
        };
      });
  }, [postContractList]);

  const toggle = (patternNo) => {
    setExpanded((prev) => ({ ...prev, [patternNo]: !prev[patternNo] }));
  };

  const yen = (n) => Number(n || 0).toLocaleString('ja-JP');

  const clientId = location?.state?.clientId;
  const backPage = location?.pathname;

  // 新規パターン作成：契約済見積をコピー
  const handleNewPattern = () => {
    const baseQuoteNo = contractList?.[0]?.QUOTE_NO;
    if (!baseQuoteNo) return;
    navigate('/admin/CustomerQuoteEntry', {
      state: {
        mode: 'cpy',
        clientId,
        seikyuNo: selectedSeikyuNo,
        quoteNo: baseQuoteNo,
        postContract: true,
        pcMode: 'newPattern',
        baseQuoteNo,
        backPage,
      },
    });
  };

  // 改訂：選択見積をコピーして同パターンの新版
  const handleRevise = (item) => {
    navigate('/admin/CustomerQuoteEntry', {
      state: {
        mode: 'cpy',
        clientId,
        seikyuNo: selectedSeikyuNo,
        quoteNo: item.QUOTE_NO,
        postContract: true,
        pcMode: 'revise',
        patternNo: item.PATTERN_NO,
        backPage,
      },
    });
  };

  // コピー：選択見積を元に新パターンを派生
  const handleCopy = (item) => {
    navigate('/admin/CustomerQuoteEntry', {
      state: {
        mode: 'cpy',
        clientId,
        seikyuNo: selectedSeikyuNo,
        quoteNo: item.QUOTE_NO,
        postContract: true,
        pcMode: 'newPattern',
        baseQuoteNo: item.QUOTE_NO,
        backPage,
      },
    });
  };

  // 編集・削除・承認申請・申請取消
  const handleEdit = (mode, item) => {
    navigate('/admin/CustomerQuoteEntry', {
      state: {
        mode,
        clientId,
        seikyuNo: selectedSeikyuNo,
        quoteNo: item.QUOTE_NO,
        postContract: true,
        patternNo: item.PATTERN_NO,
        backPage,
      },
    });
  };

  // 採用 / 採用解除
  const handleAdopt = (item) => {
    restApi.post(`/api/quote/adopt/${item.QUOTE_NO}`, {}, (_) => {
      if (callback) callback();
    });
  };

  // 印刷・ダウンロード
  const handlePrint = (item, printFlg) => {
    const basePath = '/api/quote/postcontract/report/';
    const fileName = `変更見積書_${item.QUOTE_NO}.xlsx`;
    if (printFlg) {
      restApi.postToPdf(`${basePath}${item.QUOTE_NO}`, { printFlg }, () => { });
    } else {
      restApi.postDownload(`${basePath}${item.QUOTE_NO}`, { printFlg }, fileName, () => { });
    }
  };

  // アイコンの使用可否
  const isDisabled = (icon, item) => {
    const s = item.STATUS;
    switch (icon) {
      case '編集':
      case '削除':
        return s == '承認待ち' || s == '承認済み' || item.ADOPTED_FLG == 1;
      case '承認申請':
        // 承認待ち・承認済み・小工事(承認不要)のときだけ不可。作成中/却下は申請可
        return s == '承認待ち' || s == '承認済み' || s == '小工事';
      case '承認申請取消':
        return s != '承認待ち';
      case '採用': {
        if (item.ADOPTED_FLG == 1) return false;
        // 縦フォーマットは制限なく採用可。横フォーマットは承認済み・小工事(承認不要)のみ
        if (item.QUOTE_FORMAT_TYPE !== 1) return false;
        return s != '承認済み' && s != '小工事';
      }
      default:
        return false;
    }
  };

  const renderRow = (row, pattern, isHistory) => (
    <tr
      key={row.QUOTE_NO}
      style={{ background: isHistory ? '#f7f7f7' : '#fff', color: isHistory ? '#888' : 'inherit' }}
    >
      {/* 開閉トグル */}
      <td style={{ ...tdBase, textAlign: 'center' }}>
        {!isHistory && pattern.history.length > 0 ? (
          <span
            className="material-symbols-rounded"
            style={{ cursor: 'pointer', userSelect: 'none', fontSize: 20, verticalAlign: 'middle' }}
            title={expanded[pattern.patternNo] ? '履歴を閉じる' : '履歴を開く'}
            onClick={() => toggle(pattern.patternNo)}
          >
            {expanded[pattern.patternNo] ? 'expand_more' : 'chevron_right'}
          </span>
        ) : (
          ''
        )}
      </td>
      <td style={tdBase}>
        {row.SYANAI_MEI}
      </td>
      <td style={{ ...tdBase, textAlign: 'right' }}>{row.QUOTE_NO}</td>
      <td style={tdBase}>{row.MITSU_DATE}</td>
      <td style={{ ...tdBase, textAlign: 'right' }}>{yen(row.MITSU_KIN)}</td>
      <td style={{ ...tdBase, textAlign: 'right' }}>{yen(row.MITSU_KIN_ZEI)}</td>
      <td style={{ ...tdBase, textAlign: 'center' }}>
        <StatusLableComponent status={row.STATUS} rejectReason={row.REJECT_REASON} />
      </td>
      <td style={{ ...tdBase, textAlign: 'center' }}>
        <span
          className="material-symbols-rounded"
          title={row.ADOPTED_FLG == 1 ? '採用中（クリックで解除）' : '採用する'}
          style={{
            cursor: isDisabled('採用', row) ? 'default' : 'pointer',
            color: row.ADOPTED_FLG == 1 ? '#f0ad4e' : isDisabled('採用', row) ? '#ccc' : '#888',
            fontVariationSettings: row.ADOPTED_FLG == 1 ? "'FILL' 1" : "'FILL' 0",
            verticalAlign: 'middle',
          }}
          onClick={() => {
            if (!isDisabled('採用', row)) handleAdopt(row);
          }}
        >
          star
        </span>
      </td>
      <td style={{ ...tdBase, whiteSpace: 'nowrap', overflow: 'visible' }}>
        {!isHistory && (
          <Button
            className="cell-icon btn-fill"
            variant="primary"
            title="改訂"
            onClick={() => handleRevise(row)}
          >
            改
          </Button>
        )}
        <Button
          className="material-symbols-rounded cell-icon btn-fill"
          variant="primary"
          title="コピー"
          onClick={() => handleCopy(row)}
        >
          file_copy
        </Button>
        {!isHistory && (
          <>
            <Button
              className="material-symbols-rounded cell-icon btn-fill"
              variant="primary"
              title="編集"
              onClick={() => handleEdit('upd', row)}
              disabled={isDisabled('編集', row)}
            >
              edit
            </Button>
            <Button
              className="material-symbols-rounded cell-icon btn-fill"
              variant="danger"
              title="削除"
              onClick={() => handleEdit('del', row)}
              disabled={isDisabled('削除', row)}
            >
              delete
            </Button>
            <Button
              className="cell-icon btn-fill"
              variant="primary"
              title="承認申請"
              onClick={() => handleEdit('request', row)}
              disabled={isDisabled('承認申請', row)}
            >
              申
            </Button>
            <Button
              className="cell-icon btn-fill"
              variant="danger"
              title="承認申請取消"
              onClick={() => handleEdit('cancel', row)}
              disabled={isDisabled('承認申請取消', row)}
            >
              消
            </Button>
          </>
        )}
        <Button
          className="material-symbols-rounded cell-icon btn-fill"
          variant={row?.QUOTE_FORMAT_TYPE === 1 ? 'primary' : 'warning'}
          title="印刷"
          onClick={() => handlePrint(row, true)}
        >
          print
        </Button>
        <Button
          className="material-symbols-rounded cell-icon btn-fill"
          variant={row?.QUOTE_FORMAT_TYPE === 1 ? 'primary' : 'warning'}
          title="Excelダウンロード"
          onClick={() => handlePrint(row, false)}
        >
          download
        </Button>
      </td>
    </tr>
  );

  return (
    <>
      <Row>
        <Col>
          <div className="list-label1">契約後見積書変更一覧</div>
        </Col>
        <Col></Col>
        <Row>
          <Col className="mr-3 pt-0 d-flex flex-nowrap text-right align-items-end">
            <Button
              className="material-symbols-rounded cell-icon btn-fill"
              variant="primary"
              title="契約後見積作成"
              onClick={handleNewPattern}
              disabled={contractList?.length == 0}
            >
              add
            </Button>
          </Col>
        </Row>
      </Row>
      <Row>
        <Col>
          <div className="list-3-line">
            <table style={tableStyle}>
              <colgroup>
                <col style={{ width: 40 }} />
                <col />
                <col style={{ width: 90 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 56 }} />
                <col style={{ width: 300 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle}></th>
                  <th style={thStyle}>工事件名</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>見積番号</th>
                  <th style={thStyle}>作成日</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>金額(税抜)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>金額(税込)</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>ステータス</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>採用</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {patternList.map((pattern) => (
                  <Fragment key={pattern.patternNo}>
                    {/* 最新版 */}
                    {renderRow(pattern.latest, pattern, false)}
                    {/* 旧版（展開時のみ表示） */}
                    {expanded[pattern.patternNo] &&
                      pattern.history.map((row) => renderRow(row, pattern, true))}
                    {/* 件数ヒント */}
                    {!expanded[pattern.patternNo] && pattern.history.length > 0 && (
                      <tr style={{ background: '#fff' }}>
                        <td style={tdBase}></td>
                        <td style={{ ...tdBase, color: '#888' }} colSpan={8}>
                          <span style={{ cursor: 'pointer' }} onClick={() => toggle(pattern.patternNo)}>
                            ▸ 旧版 {pattern.history.length} 件（クリックで表示）
                          </span>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {patternList.length == 0 && (
                  <tr>
                    <td style={{ ...tdBase, color: '#888', textAlign: 'center' }} colSpan={9}>
                      契約後の見積書変更はありません。
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
    </>
  );
}

export default PostContractQuoteInfo;
